/**
 * 자료이관 > 더존G20 > 기초이월자료
 * 기초이월 엑셀을 올려 그리드(화면 분량)로 확인하고, 변환 후 aslpfwd_dtl / aslpviw 엑셀을 받는다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getFwdStore,
  parseG20FwdWorkbook,
  filterFwdRows,
  sliceForView,
  convertFwdRows,
  downloadFwdWorkbook,
  downloadG20FwdTemplate,
  sourceFwdGridColumns,
} from 'migrate/g20FwdConvert';

const GRID_ID = 'mig-g20-fwd';

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 4px 2px 6px;
  flex-shrink: 0;
`;

const GridWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

function formatAmt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('ko-KR');
}

function G20FwdConvertInner() {
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [hint, setHint] = useState('기초이월 엑셀을 업로드하세요. 그리드는 화면 분량만 표시합니다.');
  const [busy, setBusy] = useState(false);

  const searchDefaultValues = useMemo(
    () => ({
      tenantCode,
      fiscalYear: '',
      keyword: '',
      pageSize: String(DEFAULT_PAGE_SIZE),
      page: 1,
    }),
    [tenantCode]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaultValues });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const columns = useMemo(() => sourceFwdGridColumns(hiCol), []);

  const loadWindow = useCallback(() => {
    const store = getFwdStore();
    const values = getValues() || {};
    const filtered = filterFwdRows(store.sourceRows, values);
    const win = sliceForView(filtered, values.page, values.pageSize);
    if (win.page !== Number(values.page)) setValue('page', win.page);
    const fileBit = store.fileName ? `파일 ${store.fileName}` : '미업로드';
    const skipBit = store.skipped ? ` · 합계행 제외 ${formatAmt(store.skipped)}건` : '';
    const mapBit = store.accMap?.size ? ` · 계정마스터 ${formatAmt(store.accMap.size)}건` : '';
    setHint(
      `${fileBit} · 전체 ${formatAmt(win.total)}건${skipBit}${mapBit}` +
        (win.total
          ? ` · 화면 ${formatAmt(win.start + 1)}–${formatAmt(win.end)} · ${win.page}/${win.totalPages}페이지`
          : '')
    );
    return win.viewRows;
  }, [getValues, setValue]);

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    customOnSearch: async () => loadWindow(),
  });

  const refreshWindow = useCallback(async () => {
    await grid.handleSearch();
  }, [grid]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '기초이월(화면)',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: 'G20기초이월_화면' });

  const handleSearch = useCallback(
    async (opts = {}) => {
      const store = getFwdStore();
      if (!store.sourceRows.length) {
        if (!opts.silent) showAlert('먼저 기초이월 엑셀을 업로드하세요.', 'warning');
        return;
      }
      setValue('page', 1);
      try {
        await refreshWindow();
      } catch (e) {
        showAlert(e.message || '조회 실패', 'error');
      }
    },
    [setValue, refreshWindow]
  );

  const handlePage = useCallback(
    async (delta) => {
      const store = getFwdStore();
      if (!store.sourceRows.length) {
        showAlert('먼저 기초이월 엑셀을 업로드하세요.', 'warning');
        return;
      }
      const values = getValues() || {};
      const filtered = filterFwdRows(store.sourceRows, values);
      const cur = sliceForView(filtered, values.page, values.pageSize);
      const next = Math.min(cur.totalPages, Math.max(1, cur.page + delta));
      if (next === cur.page) return;
      setValue('page', next);
      try {
        await refreshWindow();
      } catch (e) {
        showAlert(e.message || '조회 실패', 'error');
      }
    },
    [getValues, setValue, refreshWindow]
  );

  const handleTemplate = useCallback(() => {
    downloadG20FwdTemplate();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setBusy(true);
      grid.setLoading?.(true);
      setHint(`엑셀 읽는 중… ${file.name}`);
      try {
        const buf = await file.arrayBuffer();
        const parsed = await parseG20FwdWorkbook(buf, {
          tenantCode,
          fileName: file.name,
          fiscalYear: getValues('fiscalYear'),
          onProgress: ({ loaded, totalHint }) => {
            if (totalHint) setHint(`엑셀 변환 중… ${formatAmt(loaded)} / ${formatAmt(totalHint)}`);
            else setHint(`엑셀 변환 중… ${formatAmt(loaded)}건`);
          },
        });
        if (parsed.years?.length === 1) setValue('fiscalYear', parsed.years[0]);
        setValue('page', 1);
        setValue('keyword', '');
        await refreshWindow();
        const warn = (parsed.warnings || []).length ? `\n${parsed.warnings.join('\n')}` : '';
        const mapBit = parsed.mapCount ? `\n계정마스터 ${formatAmt(parsed.mapCount)}건` : '';
        showAlert(
          `${file.name}\n기초이월 ${formatAmt(parsed.rowCount)}건을 올렸습니다.` +
            (parsed.skipped ? `\n합계행 등 ${formatAmt(parsed.skipped)}건은 제외했습니다.` : '') +
            `${mapBit}\n화면에는 ${getValues('pageSize')}건씩 표시합니다.${warn}`,
          parsed.warnings?.length ? 'warning' : 'success'
        );
      } catch (err) {
        showAlert(err.message || '엑셀을 읽지 못했습니다.', 'error');
        setHint('업로드에 실패했습니다. *계정과목·차변 헤더를 확인하세요.');
      } finally {
        setBusy(false);
        grid.setLoading?.(false);
      }
    },
    [tenantCode, grid, refreshWindow, setValue, getValues]
  );

  const handleConvert = useCallback(async () => {
    const store = getFwdStore();
    if (!store.sourceRows.length) {
      showAlert('먼저 기초이월 엑셀을 업로드하세요.', 'warning');
      return;
    }
    const values = getValues() || {};
    const filtered = filterFwdRows(store.sourceRows, { fiscalYear: values.fiscalYear, keyword: '' });
    if (!filtered.length) {
      showAlert('변환할 행이 없습니다. 회계연도 조건을 확인하세요.', 'warning');
      return;
    }
    setBusy(true);
    grid.setLoading?.(true);
    try {
      const converted = await convertFwdRows(filtered, {
        tenantCode,
        fiscalYear: values.fiscalYear,
        accMap: store.accMap,
        onProgress: ({ done, total }) => {
          if (total) setHint(`변환 중… ${formatAmt(done)} / ${formatAmt(total)}`);
        },
      });
      const s = converted.summary;
      const base = String(store.fileName || 'G20기초이월').replace(/\.(xlsx|xls)$/i, '');
      setHint('엑셀 파일 작성 중…');
      await downloadFwdWorkbook(converted, `${base}_ERP기초이월.xlsx`, ({ done, total }) => {
        if (total) setHint(`엑셀 작성 중… ${formatAmt(done)} / ${formatAmt(total)}`);
      });
      await refreshWindow();
      const unbal = s.balanced ? '' : `\n차대 불일치 ${formatAmt(s.diffAmt)}원 — 합계행(기입력건) 제외 여부 확인`;
      const miss = s.missingAccCnt
        ? `\n계정마스터 미매핑 ${formatAmt(s.missingAccCnt)}건은 '계정미매핑' 시트를 확인하세요.`
        : '';
      showAlert(
        `변환 완료\n기초이월 ${formatAmt(s.rowCnt)}건\n차변 ${formatAmt(s.sumDr)} · 대변 ${formatAmt(s.sumCr)}${unbal}${miss}`,
        !s.balanced || s.missingAccCnt ? 'warning' : 'success'
      );
    } catch (err) {
      showAlert(err.message || '변환에 실패했습니다.', 'error');
    } finally {
      setBusy(false);
      grid.setLoading?.(false);
    }
  }, [tenantCode, getValues, grid, refreshWindow]);

  const actionButtons = useMemo(
    () => [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: () => handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
        hotkey: 'F7',
        disabled: busy,
      },
      excelButton,
    ],
    [handleSearch, excelButton, busy]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'tpl',
        label: '양식다운로드',
        icon: '📄',
        onClick: handleTemplate,
        title: '기초이월 업로드 헤더 샘플',
        disabled: busy,
      },
      {
        id: 'upload',
        label: '엑셀업로드',
        icon: '📤',
        onClick: handleUploadClick,
        title: 'G20 기초이월관리 엑셀',
        disabled: busy,
      },
      {
        id: 'prev',
        label: '이전',
        icon: '◀',
        onClick: () => handlePage(-1),
        title: '이전 페이지',
        disabled: busy,
      },
      {
        id: 'next',
        label: '다음',
        icon: '▶',
        onClick: () => handlePage(1),
        title: '다음 페이지',
        disabled: busy,
      },
      {
        id: 'convert',
        label: '변환',
        icon: '🔁',
        onClick: () => handleConvert().catch((e) => showAlert(e.message || '변환 실패', 'error')),
        title: 'aslpfwd_dtl / aslpviw 엑셀 다운로드',
        variant: 'success',
        disabled: busy,
      },
    ],
    [handleTemplate, handleUploadClick, handlePage, handleConvert, busy]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'select',
          options: [
            { value: '', label: '전체' },
            ...Array.from({ length: 14 }, (_, i) => {
              const y = String(2013 + i);
              return { value: y, label: y };
            }),
          ],
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'keyword',
          label: '검색어',
          type: 'text',
          width: '200px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '계정·거래처·부서·계좌',
        },
        {
          name: 'pageSize',
          label: '화면건수',
          type: 'select',
          options: PAGE_SIZE_OPTIONS,
          width: '110px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup
          buttons={actionButtons}
          extraButtons={extraButtons}
          loading={busy || grid.loading}
        />
        <CollapsibleSearchPanel
          storageKey="mig-g20-fwd"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea compact fields={searchFields} onFieldEnter={() => handleSearch({ silent: true })} />
        </CollapsibleSearchPanel>
      </FormProvider>
      <Meta>{hint}</Meta>
      <GridWrap>
        <GridExportFocusScope gridId={GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu={false}
            showModColumn={false}
            enableCut={false}
            enableFindReplace
            allowReplace={false}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            useVirtualRows
            exportFileName="G20기초이월_화면"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
          />
        </GridExportFocusScope>
      </GridWrap>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleUploadFile}
      />
      {excelDialog}
    </PageContainer>
  );
}

const G20FwdConvert = () => (
  <GridExportProvider>
    <G20FwdConvertInner />
  </GridExportProvider>
);

export default G20FwdConvert;
